import { useState, useMemo } from 'react';
import { Calculator, Copy, Check } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

// v3.15-965: Fee processor presets. `flat` is a per-transaction surcharge in
// dollars (e.g. Helcim's $0.10). When set, the formula adjusts to:
//   adjusted = (target - flat) / (1 + pct/100)
// so the rep can model processors that combine % + flat fee.
const FEE_PRESETS = [
  { key: 'bill',    label: 'Bill — 2.99%',          pct: 2.99, flat: 0    },
  { key: 'stripe',  label: 'Stripe — 2.9% + $0.30', pct: 2.9,  flat: 0.30 },
  { key: 'square',  label: 'Square — 2.6% + $0.10', pct: 2.6,  flat: 0.10 },
  { key: 'helcim',  label: 'Helcim — 2.49% + $0.10', pct: 2.49, flat: 0.10 },
  { key: 'paypal',  label: 'PayPal — 3.49% + $0.49', pct: 3.49, flat: 0.49 },
  { key: 'custom',  label: 'Custom...',              pct: null, flat: null },
];

// v3.15-964: Credit Card Fee Calculator.
// Formula:    Adjusted = (Target − Flat) / (1 + Pct%)
// Default:    Flat = 0 → behaves like the original Adjusted = Target / (1 + Pct%)
// Example:    10601 / 1.0299 = $10,293.23
//
// Pure frontend, no API calls. Sits inside the Credit Card Authorization
// block so reps can quickly compute the amount to enter into Bill.
//
// `defaultTarget` is optional — passing the recorded card amount pre-fills
// the calculator so reps don't retype it.
export const CreditCardFeeCalculator = ({ defaultTarget = '', defaultFeePct = 2.99 }) => {
  const [target, setTarget] = useState(defaultTarget ? String(defaultTarget) : '');
  const [feePct, setFeePct] = useState(String(defaultFeePct));
  const [feeFlat, setFeeFlat] = useState('0');
  const [presetKey, setPresetKey] = useState('bill');
  const [copied, setCopied] = useState(false);

  const handlePresetChange = (key) => {
    setPresetKey(key);
    const preset = FEE_PRESETS.find((p) => p.key === key);
    if (preset && preset.pct !== null) {
      setFeePct(String(preset.pct));
      setFeeFlat(String(preset.flat));
    }
    // 'custom' leaves the inputs untouched so the rep can type freely.
  };

  const result = useMemo(() => {
    const t    = parseFloat(target);
    const f    = parseFloat(feePct);
    const flat = parseFloat(feeFlat) || 0;
    if (Number.isNaN(t) || t <= 0) return null;
    if (Number.isNaN(f) || f < 0)  return null;
    if (flat < 0) return null;
    // (target - flat) / (1 + pct/100)
    const adjusted = (t - flat) / (1 + f / 100);
    if (adjusted <= 0) return null;
    const fee = t - adjusted;
    return {
      adjusted: Math.round(adjusted * 100) / 100,
      fee:      Math.round(fee * 100) / 100,
      total:    Math.round(t * 100) / 100,
    };
  }, [target, feePct, feeFlat]);

  const fmt = (n) =>
    n === null || n === undefined
      ? '—'
      : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.adjusted.toFixed(2));
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy.');
    }
  };

  return (
    <div
      className="rounded-md border border-slate-300 bg-white px-3 py-2.5"
      data-testid="cc-fee-calculator"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Calculator className="h-3.5 w-3.5 text-slate-600" />
          <span className="text-xs font-semibold text-slate-700">Fee Calculator</span>
        </div>
        {/* Per-processor preset — auto-fills Pct + Flat. */}
        <Select value={presetKey} onValueChange={handlePresetChange}>
          <SelectTrigger
            className="h-7 w-44 text-[11px]"
            data-testid="cc-fee-preset-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FEE_PRESETS.map((p) => (
              <SelectItem
                key={p.key}
                value={p.key}
                className="text-[11px]"
                data-testid={`cc-fee-preset-${p.key}`}
              >
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label htmlFor="cc-fee-target" className="text-[11px] text-slate-500">
            Client Charge
          </Label>
          <Input
            id="cc-fee-target"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="0.00"
            className="h-8 text-sm"
            data-testid="cc-fee-target-input"
          />
        </div>
        <div>
          <Label htmlFor="cc-fee-pct" className="text-[11px] text-slate-500">
            Fee %
          </Label>
          <Input
            id="cc-fee-pct"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={feePct}
            onChange={(e) => {
              setFeePct(e.target.value);
              setPresetKey('custom');
            }}
            placeholder="2.99"
            className="h-8 text-sm"
            data-testid="cc-fee-pct-input"
          />
        </div>
        <div>
          <Label htmlFor="cc-fee-flat" className="text-[11px] text-slate-500">
            Flat $
          </Label>
          <Input
            id="cc-fee-flat"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={feeFlat}
            onChange={(e) => {
              setFeeFlat(e.target.value);
              setPresetKey('custom');
            }}
            placeholder="0.00"
            className="h-8 text-sm"
            data-testid="cc-fee-flat-input"
          />
        </div>
      </div>

      <div className="mt-2 rounded-md bg-emerald-50 border border-emerald-200 px-2.5 py-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-emerald-900">
            Amount to Enter in Bill
          </span>
          {result && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-emerald-800 hover:bg-emerald-100"
              onClick={handleCopy}
              data-testid="cc-fee-copy-btn"
            >
              {copied ? (
                <Check className="h-3 w-3" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          )}
        </div>
        <div
          className="text-lg font-bold text-emerald-800 leading-tight"
          data-testid="cc-fee-result"
        >
          {result ? fmt(result.adjusted) : '—'}
        </div>
        {result && (
          <div className="grid grid-cols-2 gap-2 mt-1 pt-1 border-t border-emerald-100 text-[10px]">
            <div>
              <span className="text-emerald-700/70">Fee </span>
              <span
                className="font-mono font-medium text-emerald-900"
                data-testid="cc-fee-fee-amount"
              >
                {fmt(result.fee)}
              </span>
            </div>
            <div className="text-right">
              <span className="text-emerald-700/70">Final Total </span>
              <span
                className="font-mono font-medium text-emerald-900"
                data-testid="cc-fee-final-total"
              >
                {fmt(result.total)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditCardFeeCalculator;
